import React, { useState, useEffect } from 'react';
import { User, Save, X } from 'lucide-react';
import { db } from '../firebase'; // NEW: Firestore
import { doc, setDoc } from 'firebase/firestore';
import './ChatBot.css';

const UserProfileModal = ({ isOpen, onClose, onSave, initialData }) => {
    const [formData, setFormData] = useState({
        name: '',
        age: '',
        gender: 'Male',
        history: '',
        allergies: '', // NEW
        medications: '', // NEW
        activityLevel: 'Moderate', // NEW: For Nutrition Analysis
        dietPreference: 'Omnivore' // NEW: For Meal Plans
    });

    useEffect(() => {
        if (initialData) {
            setFormData(initialData);
        }
    }, [initialData]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        onSave(formData);

        // Sync to Firestore
        if (formData.name) {
            try {
                // Use Name + Age as simple ID for demo, or a real Auth ID if available. 
                // For now, simpler is better for this local demo.
                const userId = `${formData.name.toLowerCase().replace(/\s/g, '_')}_${formData.age}`;
                await setDoc(doc(db, "users", userId), {
                    ...formData,
                    updatedAt: new Date().toISOString()
                });
            } catch (error) {
                console.error("Error syncing profile to cloud:", error);
            }
        }

        onClose();
    };

    return (
        <div className="disclaimer-overlay" style={{ zIndex: 2000 }}> {/* Reuse overlay style */}
            <div className="chatbot-window" style={{ position: 'relative', width: '90%', maxWidth: '400px', height: 'auto', maxHeight: '90vh' }}>
                <div className="chatbot-header">
                    <div className="chatbot-header-title">
                        <User size={20} />
                        <h3>Your Profile</h3>
                    </div>
                    <button onClick={onClose} className="action-btn"><X size={20} /></button>
                </div>

                <form onSubmit={handleSubmit} style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '15px', overflowY: 'auto' }}>
                    <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                        Help PulseNote AI provide better advice by knowing a bit about you.
                    </p>

                    <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem', fontWeight: 'bold' }}>Name</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Your Name"
                            className="chatbot-input input" // Reusing input class logic if generic, else inline
                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-color)' }}
                            required
                        />
                    </div>

                    <div style={{ display: 'flex', gap: '10px' }}>
                        <div style={{ flex: 1 }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem', fontWeight: 'bold' }}>Age</label>
                            <input
                                type="number"
                                name="age"
                                value={formData.age}
                                onChange={handleChange}
                                placeholder="e.g. 25"
                                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-color)' }}
                                required
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem', fontWeight: 'bold' }}>Gender</label>
                            <select
                                name="gender"
                                value={formData.gender}
                                onChange={handleChange}
                                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-color)' }}
                            >
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem', fontWeight: 'bold' }}>Medical History (Optional)</label>
                        <textarea
                            name="history"
                            value={formData.history}
                            onChange={handleChange}
                            placeholder="e.g. Diabetes, Asthma..."
                            rows="2"
                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-color)', resize: 'vertical' }}
                        />
                    </div>

                    {/* NEW FIELDS */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem', fontWeight: 'bold' }}>Allergies (Critical)</label>
                        <input
                            type="text"
                            name="allergies"
                            value={formData.allergies}
                            onChange={handleChange}
                            placeholder="e.g. Penicillin, Peanuts, None"
                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #fed7d7', background: 'var(--bg-secondary)', color: 'var(--text-color)' }}
                        />
                    </div>

                    <div>
                        <textarea
                            name="medications"
                            value={formData.medications}
                            onChange={handleChange}
                            placeholder="e.g. Metformin 500mg, Paracetamol..."
                            rows="2"
                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-color)', resize: 'vertical' }}
                        />
                    </div>

                    <div style={{ display: 'flex', gap: '10px' }}>
                        <div style={{ flex: 1 }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem', fontWeight: 'bold' }}>Activity Level</label>
                            <select
                                name="activityLevel"
                                value={formData.activityLevel}
                                onChange={handleChange}
                                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-color)' }}
                            >
                                <option value="Sedentary">Sedentary (Low)</option>
                                <option value="Moderate">Moderate</option>
                                <option value="Active">Active (High)</option>
                            </select>
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem', fontWeight: 'bold' }}>Diet Preference</label>
                            <select
                                name="dietPreference"
                                value={formData.dietPreference}
                                onChange={handleChange}
                                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-color)' }}
                            >
                                <option value="Omnivore">Omnivore</option>
                                <option value="Vegetarian">Vegetarian</option>
                                <option value="Vegan">Vegan</option>
                                <option value="Keto">Keto</option>
                            </select>
                        </div>
                    </div>

                    <button type="submit" className="book-btn" style={{ width: '100%', justifyContent: 'center', marginTop: '10px' }}>
                        <Save size={18} /> Save Profile
                    </button>
                </form>
            </div>
        </div>
    );
};

export default UserProfileModal;
